import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {Bindings} from "data/constants/bindings";
import {ContestStatus} from "data/enums";
import {first, last} from "lodash";
import {Empty} from "data/types/global";

export interface IContest {
	id: number;
	status: ContestStatus;
	date: string;
	roundId: number;
}

export interface IContestsStore {
	get getIsLoading(): boolean;

	get list(): IContest[];

	get openContests(): IContest[];

	get scheduledContests(): IContest[];

	get nextScheduledContest(): IContest | undefined;

	get nextCurrentContest(): IContest | undefined;

	get gameContests(): IContest[];

	get currentContest(): IContest | undefined;

	get currentGameContest(): IContest | undefined;

	get isPrevContestAvailable(): boolean;

	get isNextContestAvailable(): boolean;

	toPrevContest(): void;

	toNextContest(): void;

	toContestId(contestId: number): void;

	clearContest(): void;

	fetchContests(): Promise<void>;

	getContestById: (contestId: number) => Empty<IContest>;
}

@injectable()
export class ContestsStore implements IContestsStore {
	@observable private _isLoading: boolean = false;
	@observable private _selectedContest: IContest | undefined;
	@observable private _list: IContest[] = [];

	constructor(@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider) {
		makeAutoObservable(this);
	}

	get list() {
		return this._list;
	}

	get openContests() {
		return this._list.filter((e) => e.status === ContestStatus.Open);
	}

	get scheduledContests(): IContest[] {
		return this._list.filter((e) => e.status === ContestStatus.Scheduled);
	}

	get nextScheduledContest(): IContest | undefined {
		return first(this.scheduledContests);
	}

	get nextCurrentContest(): IContest | undefined {
		const currentContestIndex = this.list.findIndex((it) => it.id === this.currentContest?.id);

		return this.list[currentContestIndex + 1];
	}

	get completedContests() {
		return this._list.filter((e) => e.status === ContestStatus.Complete);
	}

	get gameContests() {
		return this._list.filter((e) =>
			[ContestStatus.Complete, ContestStatus.Live].includes(e.status)
		);
	}

	get getIsLoading(): boolean {
		return this._isLoading;
	}

	get currentContest() {
		return this._selectedContest || this.currentGameContest;
	}

	get currentGameContest() {
		return this.activeContest || last(this.completedContests) || first(this.openContests);
	}

	get isNextContestAvailable(): boolean {
		if (!this.nextContest) {
			return false;
		}
		return this.nextContest.status !== ContestStatus.Open;
	}

	get isPrevContestAvailable(): boolean {
		if (!this.previousContest) {
			return false;
		}
		return this.previousContest.status !== ContestStatus.Open;
	}

	private get activeContest() {
		return this.list.find((e) => e.status === ContestStatus.Live);
	}

	private get previousContest(): IContest | undefined {
		const currentContestIndex = this.list.findIndex((it) => it.id === this.currentContest?.id);
		return this.list[currentContestIndex - 1];
	}

	private get nextContest(): IContest | undefined {
		const currentContestIndex = this.list.findIndex((it) => it.id === this.currentContest?.id);
		return this.list[currentContestIndex + 1];
	}

	@action
	async fetchContests() {
		const {data} = await this._jsonProvider.contests();

		runInAction(() => {
			this._list = data.items;
		});
	}

	@action
	public toNextContest() {
		if (!this.nextContest) {
			return;
		}
		this._selectedContest = this.nextContest;
	}

	@action
	public toPrevContest() {
		if (!this.previousContest) {
			return;
		}
		this._selectedContest = this.previousContest;
	}

	public toContestId(contestId: number) {
		const contest = this.getContestById(contestId);
		if (contest) {
			this._selectedContest = contest;
		}
	}

	public getContestById = (contestId: number) => {
		return this.list.find((contest) => contest.id === contestId);
	};

	@action
	public clearContest() {
		this._selectedContest = undefined;
	}
}
